import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiX, FiCalendar, FiUpload, FiCheck, FiFile, FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { GoOrganization, GoCheck, GoSync } from 'react-icons/go';
import { toast } from 'react-toastify';
import api from '../../../services/api';

export default function NovoRelatorioModal({ isOpen, onClose, onComplete }) {
  const [step, setStep] = useState(1); // 1 = Seleção, 2 = Upload
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
      // Definir data padrão como mês atual
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      setStartDate(formatDateForInput(firstDay));
      setEndDate(formatDateForInput(lastDay));
    }
  }, [isOpen]);

  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const projectId = localStorage.getItem('vidal_selected_project');
      const response = await api.get(`/certificates/list/${projectId}`);
      
      if (response.data && response.data.certificates) {
        const companies = response.data.certificates.map((cert, index) => ({
          id: cert.cnpj,
          cnpj: cert.cnpj,
          name: cert.razaoSocial || cert.cnpj,
          color: getCompanyColor(index)
        }));
        setAvailableCompanies(companies);
      }
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const getCompanyColor = (index) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899'];
    return colors[index % colors.length];
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId) 
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const handleContinue = () => {
    if (!startDate || !endDate) {
      toast.error('Selecione o período');
      return;
    }
    if (selectedCompanies.length === 0) {
      toast.error('Selecione pelo menos uma empresa');
      return;
    }
    setStep(2);
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    
    for (const file of files) {
      if (!file.name.toLowerCase().endsWith('.txt')) {
        toast.error(`Arquivo ${file.name} não é um arquivo SPED válido (.txt)`);
        continue;
      }

      // Adicionar arquivo à lista com status "uploading"
      const fileId = Date.now() + Math.random();
      setUploadedFiles(prev => [...prev, {
        id: fileId,
        name: file.name,
        size: file.size,
        status: 'uploading',
        progress: 0
      }]);

      try {
        const formData = new FormData();
        formData.append('sped', file);
        formData.append('startDate', startDate);
        formData.append('endDate', endDate);
        formData.append('companies', JSON.stringify(selectedCompanies));

        // Simular upload (substituir pela chamada real da API)
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Atualizar status para sucesso
        setUploadedFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, status: 'success', progress: 100 } : f
        ));

        toast.success(`${file.name} enviado com sucesso!`);
      } catch (error) {
        console.error('Erro ao fazer upload:', error);
        setUploadedFiles(prev => prev.map(f => 
          f.id === fileId ? { ...f, status: 'error', progress: 0 } : f
        ));
        toast.error(`Erro ao enviar ${file.name}`);
      }
    }
  };

  const handleClose = () => {
    setStep(1);
    setStartDate('');
    setEndDate('');
    setSelectedCompanies([]);
    setUploadedFiles([]);
    onClose();
  };

  const handleFinish = () => {
    if (uploadedFiles.length === 0) {
      toast.error('Envie pelo menos um arquivo SPED');
      return;
    }
    
    const successFiles = uploadedFiles.filter(f => f.status === 'success');
    if (successFiles.length === 0) {
      toast.error('Nenhum arquivo foi enviado com sucesso');
      return;
    }

    toast.success(`${successFiles.length} arquivo(s) processado(s) com sucesso!`);
    onComplete && onComplete();
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <h2>{step === 1 ? 'Novo Relatório SPED' : 'Enviar Arquivos SPED'}</h2>
          <CloseButton onClick={handleClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          {step === 1 ? (
            <>
              {/* Etapa 1: Seleção de Período e Empresas */}
              <Section>
                <SectionTitle>
                  <FiCalendar />
                  Período do Relatório
                </SectionTitle>
                <DateInputs>
                  <DateField>
                    <label>Data Início</label>
                    <input 
                      type="date" 
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </DateField>
                  <DateField>
                    <label>Data Fim</label>
                    <input 
                      type="date" 
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </DateField>
                </DateInputs>
              </Section>

              <Section>
                <SectionTitle>
                  <GoOrganization />
                  Empresas ({selectedCompanies.length} selecionadas)
                </SectionTitle>
                
                {loadingCompanies ? (
                  <LoadingState>
                    <GoSync className="spin" />
                    Carregando empresas...
                  </LoadingState>
                ) : (
                  <CompaniesGrid>
                    {availableCompanies.map(company => (
                      <CompanyCard
                        key={company.id}
                        selected={selectedCompanies.includes(company.id)}
                        color={company.color}
                        onClick={() => toggleCompany(company.id)}
                      >
                        <CompanyAvatar color={company.color}>
                          {company.name.substring(0, 2).toUpperCase()}
                        </CompanyAvatar>
                        <CompanyInfo>
                          <CompanyName>{company.name}</CompanyName>
                          <CompanyCnpj>{company.cnpj}</CompanyCnpj>
                        </CompanyInfo>
                        {selectedCompanies.includes(company.id) && (
                          <CheckIcon>
                            <GoCheck />
                          </CheckIcon>
                        )}
                      </CompanyCard>
                    ))}
                  </CompaniesGrid>
                )}
              </Section>
            </>
          ) : (
            <>
              {/* Etapa 2: Upload de Arquivos */}
              <Section>
                <PeriodSummary>
                  <FiCalendar />
                  <span>
                    Período: <strong>{new Date(startDate).toLocaleDateString('pt-BR')} - {new Date(endDate).toLocaleDateString('pt-BR')}</strong>
                  </span>
                  <span className="separator">•</span>
                  <GoOrganization />
                  <span>
                    <strong>{selectedCompanies.length}</strong> empresa(s) selecionada(s)
                  </span>
                </PeriodSummary>
              </Section>

              <Section>
                <SectionTitle>
                  <FiUpload />
                  Enviar Arquivos SPED
                </SectionTitle>
                
                <UploadArea>
                  <input
                    type="file"
                    id="sped-upload"
                    accept=".txt"
                    multiple
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="sped-upload">
                    <UploadButton as="div">
                      <FiUpload />
                      Selecionar Arquivos SPED (.txt)
                    </UploadButton>
                  </label>
                </UploadArea>

                {uploadedFiles.length > 0 && (
                  <FilesList>
                    {uploadedFiles.map(file => (
                      <FileItem key={file.id} status={file.status}>
                        <FileIcon status={file.status}>
                          {file.status === 'uploading' && <GoSync className="spin" />}
                          {file.status === 'success' && <GoCheck />}
                          {file.status === 'error' && <FiX />}
                        </FileIcon>
                        <FileInfo>
                          <FileName>{file.name}</FileName>
                          <FileSize>{(file.size / 1024).toFixed(2)} KB</FileSize>
                        </FileInfo>
                        <FileStatus status={file.status}>
                          {file.status === 'uploading' && 'Enviando...'}
                          {file.status === 'success' && 'Enviado'}
                          {file.status === 'error' && 'Erro'}
                        </FileStatus>
                      </FileItem>
                    ))}
                  </FilesList>
                )}
              </Section>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {step === 1 ? (
            <>
              <CancelButton onClick={handleClose}>Cancelar</CancelButton>
              <ContinueButton onClick={handleContinue}>
                Continuar
                <FiArrowRight />
              </ContinueButton>
            </>
          ) : (
            <>
              <BackButton onClick={() => setStep(1)}>
                <FiArrowLeft />
                Voltar
              </BackButton>
              <FinishButton onClick={handleFinish}>
                Finalizar
              </FinishButton>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.body};
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid ${({ theme }) => theme.border};

  h2 {
    margin: 0;
    font-size: 1.4em;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5em;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
`;

const Section = styled.div`
  margin-bottom: 28px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px 0;
  font-size: 1.05em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};

  svg {
    font-size: 1.2em;
    color: #10b981;
  }
`;

const DateInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const DateField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 0.9em;
    font-weight: 500;
    color: ${({ theme }) => theme.text};
    opacity: 0.8;
  }

  input {
    padding: 12px 14px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    background: ${({ theme }) => theme.body};
    color: ${({ theme }) => theme.text};
    font-size: 0.95em;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #10b981;
      box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
    }
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CompaniesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding: 4px;
`;

const CompanyCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 2px solid ${({ selected, color }) => selected ? color : 'transparent'};
  background: ${({ theme, selected }) => 
    selected ? theme.body : (theme.body === '#fafbfc' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'};
    transform: translateY(-2px);
  }
`;

const CompanyAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${({ color }) => color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9em;
  flex-shrink: 0;
`;

const CompanyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyName = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CompanyCnpj = styled.div`
  font-size: 0.8em;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  margin-top: 2px;
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #10b981;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1em;
  flex-shrink: 0;
`;

const PeriodSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(16, 185, 129, 0.12)'};
  border-radius: 12px;
  color: ${({ theme }) => theme.text};
  font-size: 0.95em;

  svg {
    color: #10b981;
    font-size: 1.2em;
  }

  .separator {
    opacity: 0.3;
  }

  strong {
    color: #10b981;
  }
`;

const UploadArea = styled.div`
  margin-bottom: 20px;
`;

const UploadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  }

  svg {
    font-size: 1.2em;
  }
`;

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'};
  border-radius: 12px;
  border-left: 3px solid ${({ status }) => 
    status === 'success' ? '#10b981' : 
    status === 'error' ? '#ef4444' : 
    '#3b82f6'};
`;

const FileIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: ${({ status }) => 
    status === 'success' ? 'rgba(16, 185, 129, 0.1)' : 
    status === 'error' ? 'rgba(239, 68, 68, 0.1)' : 
    'rgba(59, 130, 246, 0.1)'};
  color: ${({ status }) => 
    status === 'success' ? '#10b981' : 
    status === 'error' ? '#ef4444' : 
    '#3b82f6'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  flex-shrink: 0;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FileSize = styled.div`
  font-size: 0.8em;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  margin-top: 2px;
`;

const FileStatus = styled.div`
  font-size: 0.85em;
  font-weight: 600;
  color: ${({ status }) => 
    status === 'success' ? '#10b981' : 
    status === 'error' ? '#ef4444' : 
    '#3b82f6'};
  flex-shrink: 0;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 28px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 12px;
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'};
  color: ${({ theme }) => theme.text};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
  }
`;

const BackButton = styled(Button)`
  background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'};
  color: ${({ theme }) => theme.text};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
  }
`;

const ContinueButton = styled(Button)`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  }
`;

const FinishButton = styled(Button)`
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
  }
`;
